import React, { useEffect, useId, useRef, useState } from "react";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { VerificationGate } from "../../../components/VerificationGate";
import {
    Form,
    FormCheckbox,
    FormInput,
    FormPhoneNumber,
    FormSelect,
    NonFieldErrors,
} from "../../../forms";
import { getErrorListElemID } from "../../../forms/hooks/formUtils";
import { strings } from "../../../localization";
import { FinancingPreQualRequest } from "../../../models/financing";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    trackPreQualCompleteEvent,
    trackPreQualOpenedEvent,
    trackPreQualStartedEvent,
    trackPreQualSubmitSuccessEvent,
} from "../../../utils/analytics";
import { focusElement } from "../../../utils/keyboardFocus";
import { usePageSetting } from "../../../utils/settings";
import { FinancingPreQualHero } from "../FinancingPreQualHero";
import {
    ADDRESS_TOOL_TIP_LINK,
    ADDRESS_TOOL_TIP_TEXT,
    EMAIL_TOOL_TIP_LINK,
    EMAIL_TOOL_TIP_TEXT,
    ModalStateType,
    STATE_CHOICES_WITH_SELECT_LABEL,
    WISCONSIN_STATE_CODE,
    WISCONSIN_WARNING_TEXT,
    WISCONSIN_WARNING_TITLE,
} from "../constants";
import { checkPreQualStatus, openApplyModal } from "../reducers";
import { pickVerificationPhone } from "../utils";

import styles from "./index.module.scss";

type CommonFieldProps<ValueType> = Pick<
    React.ComponentProps<typeof FormInput> &
        React.ComponentProps<typeof FormSelect>,
    "disabled" | "errors" | "showErrorMessages" | "onChange"
> & {
    value: ValueType;
};

export const FinancingPreQualForm = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const prefillData = useAppSelector((state) => state.financing.prefillData);
    const modalState = useAppSelector((state) => state.financing.modal);
    const basket = useAppSelector((state) => state.checkout.data.basket);
    const isSubmitting = modalState._tag === ModalStateType.PREQUAL_SUBMITTING;

    const hasAgreedID = useId();
    const hasStarted = useRef(false);
    const hasFiredRef = useRef(false);
    const [hasAgreed, setHasAgreed] = useState(false);
    const [consentError, setConsentError] = useState(false);
    const consentErrorID = getErrorListElemID(hasAgreedID);
    // When true, the form is hidden and the verification gate (email / SMS /
    // voice, per the enabled channels) is shown instead (server-enforced
    // fraud control on the prequal endpoint).
    const [verifying, setVerifying] = useState(false);
    const verificationRequired = !!usePageSetting(
        "financing_email_verification_required",
    );
    const [values, setValues] = useState<FinancingPreQualRequest>({
        first_name: "",
        last_name: "",
        line1: "",
        city: "",
        state: "",
        postcode: "",
        email: "",
        phone: "",
        last_four_ssn: "",
        customer_initiated: true,
    });

    const submitErrors =
        modalState._tag === ModalStateType.PREQUAL_ERROR
            ? modalState.errors
            : null;

    useEffect(() => {
        if (basket && !hasFiredRef.current) {
            hasFiredRef.current = true;
            trackPreQualOpenedEvent(basket);
        }
    }, [basket]);

    const connectProps = <T extends keyof FinancingPreQualRequest>(
        field: T,
    ): CommonFieldProps<FinancingPreQualRequest[T]> => {
        const fieldErrs = submitErrors ? submitErrors[field] : [];
        return {
            disabled: isSubmitting,
            value: values[field],
            errors: typeof fieldErrs === "string" ? [fieldErrs] : fieldErrs,
            showErrorMessages: !!submitErrors,
            onChange: (e) => {
                const value = e.currentTarget.value;
                if (!hasStarted.current) {
                    hasStarted.current = true;
                    trackPreQualStartedEvent(basket);
                }
                setValues((s) => ({
                    ...s,
                    [field]: value,
                }));
            },
        };
    };

    const onPrefill = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        setValues((s) => ({
            ...s,
            first_name: prefillData.first_name || "",
            last_name: prefillData.last_name || "",
            line1: prefillData.address_line_1 || "",
            city: prefillData.city || "",
            state: prefillData.state_code || "",
            postcode: prefillData.postal_code || "",
            email: prefillData.email || "",
            phone:
                prefillData.home_phone ||
                prefillData.mobile_phone ||
                prefillData.work_phone ||
                "",
        }));
    };

    const submitPreQual = async (): Promise<void> => {
        trackPreQualSubmitSuccessEvent(basket);
        const result = await dispatch(checkPreQualStatus(values));
        if (checkPreQualStatus.fulfilled.match(result)) {
            trackPreQualCompleteEvent(basket);
        }
    };

    const onSubmit = async (
        e: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        e.preventDefault();
        // Keep Submit in the tab order with aria-disabled (A360 #387); still
        // block the request until consent is checked, with visible/SR feedback.
        if (!hasAgreed) {
            setConsentError(true);
            // useId() values contain ':' which are valid HTML ids but break
            // querySelector inside focusElement(string). Look up by id instead.
            const checkbox = getDocument()?.getElementById(hasAgreedID);
            if (checkbox instanceof HTMLElement) {
                focusElement(checkbox, { focusVisible: true });
            }
            return;
        }
        // Before the prequal request can be submitted, the user must verify
        // ownership of their contact info (server-enforced fraud control). If
        // the account already verified (or is exempt), the gate calls
        // onVerified immediately without rendering.
        if (verificationRequired) {
            setVerifying(true);
            return;
        }
        await submitPreQual();
    };

    if (verifying) {
        return (
            <section>
                <FinancingPreQualHero />
                <div className={styles.wrapper}>
                    <VerificationGate
                        email={values.email}
                        phone={pickVerificationPhone(values.phone)}
                        onVerified={() => {
                            setVerifying(false);
                            void submitPreQual();
                        }}
                        onCancel={() => {
                            setVerifying(false);
                        }}
                    />
                </div>
            </section>
        );
    }

    return (
        <section>
            <FinancingPreQualHero />
            <div className={styles.wrapper}>
                <p>
                    <strong>
                        {t`It only takes a few minutes to find out if you
                            pre-qualify.`}
                    </strong>
                </p>
                <p>
                    {t`If you currently have a freeze on your credit file through a
                        credit reporting agency, please contact them directly to
                        remove the credit freeze before going through the
                        pre-qualification process.`}
                </p>
                {Object.keys(prefillData).length > 0 &&
                    prefillData.first_name && (
                        <button
                            className={concatClassNames([
                                "button",
                                "button--secondary",
                                styles.prefillButton,
                            ])}
                            onClick={onPrefill}
                        >
                            {t`Prefill Account Info`}
                        </button>
                    )}
                <Form
                    className={styles.form}
                    onSubmit={onSubmit}
                    noValidate={true}
                >
                    {submitErrors?.detail && (
                        <NonFieldErrors
                            errors={[submitErrors.detail]}
                            showErrorMessages={true}
                        />
                    )}
                    {submitErrors?.non_field_errors && (
                        <NonFieldErrors
                            errors={submitErrors.non_field_errors}
                            showErrorMessages={true}
                        />
                    )}
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="first_name"
                        name="first_name"
                        label={t`First Name`}
                        validation={["required", "firstname-empty"]}
                        maxLength={25}
                        autoComplete="given-name"
                        {...connectProps("first_name")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.optionalFormLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="middle_initial"
                        name="middle_initial"
                        label={t`M.I.`}
                        maxLength={2}
                        autoComplete="additional-name"
                        {...connectProps("middle_initial")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="last_name"
                        name="last_name"
                        label={t`Last Name`}
                        validation={["required", "lastname-empty"]}
                        maxLength={25}
                        autoComplete="family-name"
                        {...connectProps("last_name")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="physical_street_address"
                        name="physical_street_address"
                        label={t`Physical Street Address`}
                        validation={["required", "street-address-empty"]}
                        tooltipLink={ADDRESS_TOOL_TIP_LINK}
                        tooltipText={ADDRESS_TOOL_TIP_TEXT}
                        maxLength={25}
                        maxBytes={240}
                        autoComplete="address-line1"
                        {...connectProps("line1")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.optionalFormLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="address_line_2"
                        name="address_line_2"
                        label={t`Address Line 2`}
                        maxLength={25}
                        maxBytes={240}
                        autoComplete="address-line2"
                        {...connectProps("line2")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="city"
                        name="city"
                        label={t`City`}
                        validation={["required", "city-empty"]}
                        maxLength={60}
                        maxBytes={60}
                        autoComplete="address-level2"
                        {...connectProps("city")}
                    />
                    <FormSelect
                        className={styles.formSelect}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formSelectWrapper}
                        id="state"
                        name="state"
                        label={t`State`}
                        validation={["required", "state-empty"]}
                        choices={STATE_CHOICES_WITH_SELECT_LABEL}
                        autoComplete="address-level1"
                        warningTitle={
                            values.state === WISCONSIN_STATE_CODE
                                ? WISCONSIN_WARNING_TITLE
                                : undefined
                        }
                        warningText={
                            values.state === WISCONSIN_STATE_CODE
                                ? WISCONSIN_WARNING_TEXT
                                : undefined
                        }
                        {...connectProps("state")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="zip_code"
                        name="zip_code"
                        label={t`ZIP Code`}
                        validation={["required", "zipcode"]}
                        maxLength={5}
                        maxBytes={60}
                        autoComplete="postal-code"
                        {...connectProps("postcode")}
                    />
                    <FormPhoneNumber
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="phone"
                        id="phone_number"
                        name="phone_number"
                        label={t`Phone Number`}
                        validation={["required", "phone"]}
                        autoComplete="tel"
                        {...connectProps("phone")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        label={t`Email Address`}
                        name="email"
                        id="email"
                        validation={["required", "email"]}
                        autoComplete="email"
                        tooltipLink={EMAIL_TOOL_TIP_LINK}
                        tooltipText={EMAIL_TOOL_TIP_TEXT}
                        tooltipClass={styles.tooltipIcon}
                        isIconTrigger={true}
                        {...connectProps("email")}
                    />
                    <FormInput
                        className={styles.formField}
                        labelCSSClass={styles.formLabel}
                        wrapperCSSClass={styles.formWrapper}
                        type="text"
                        id="last_four_ssn"
                        name="last_four_ssn"
                        label={t`Last 4 digits of Social Security Number`}
                        // TODO(i18n): Use t tagged template for tooltipLink: {t`Why do we need this?`}
                        tooltipLink={"Why do we need this?"}
                        // TODO(i18n): Use t tagged template for tooltipText: {t`We ask for the last four of your Social Security number to help verify your identity.`}
                        tooltipText={
                            "We ask for the last four of your Social Security number to help verify your identity."
                        }
                        tooltipClass={styles.tooltipIcon}
                        isIconTrigger={true}
                        validation={["required", "ssn_last_four"]}
                        pattern="[0-9]*"
                        maxLength={4}
                        {...connectProps("last_four_ssn")}
                    />
                    <div className={styles.notice}>
                        {richT(
                            t`We are unable to pre-qualify customers without a Social Security number. If you have an Individual Tax Identification Number (ITIN), please skip the pre-qualification process and <A>complete a credit application using your ITIN.</A>`,
                            {
                                A: (children) => (
                                    <button
                                        type="button"
                                        className={concatClassNames([
                                            styles.link,
                                            styles.linkButton,
                                        ])}
                                        onClick={() =>
                                            dispatch(openApplyModal({}))
                                        }
                                    >
                                        {children}
                                    </button>
                                ),
                            },
                        )}
                    </div>
                    <FormCheckbox
                        id={hasAgreedID}
                        className={styles.checkbox}
                        labelCSSClass={styles.checkboxLabel}
                        wrapperCSSClass={styles.checkboxWrapper}
                        label={
                            strings.get("FINANCING_PREQUAL_CONSENT_MSG") || ""
                        }
                        hasHTML={true}
                        name="has_agreed"
                        checked={hasAgreed}
                        errors={
                            consentError
                                ? [
                                      t`Please agree to the consent terms before submitting.`,
                                  ]
                                : []
                        }
                        showErrorMessages={consentError}
                        onChange={(e) => {
                            if (!hasStarted.current) {
                                hasStarted.current = true;
                                trackPreQualStartedEvent(basket);
                            }
                            const checked = e.currentTarget.checked;
                            setHasAgreed(checked);
                            if (checked) {
                                setConsentError(false);
                            }
                        }}
                    />
                    <p className={styles.legalCopy}>
                        {t`I also understand that if Wells Fargo, N.A has no available offers, the above information will be sent to secondary lenders, and those secondary lenders may conduct a soft pull of my credit that will not affect my credit score.`}
                    </p>
                    <button
                        type="submit"
                        className={concatClassNames([
                            "button",
                            styles.submitButton,
                        ])}
                        aria-disabled={!hasAgreed}
                        aria-describedby={
                            consentError ? consentErrorID : undefined
                        }
                    >
                        {t`Submit`}
                    </button>
                </Form>
            </div>
        </section>
    );
};
